// Root of the platform admin console — reached via ?platform (see main.tsx).
// No router (react-router-dom is unused throughout this codebase, see
// App.tsx's own NAV-array approach) — a small state machine is enough for
// four screens.
import { useState } from 'react';
import { PlatformAuthProvider, usePlatformAuth } from './PlatformAuthProvider';
import LoginScreen from './LoginScreen';
import TenantListScreen from './TenantListScreen';
import TenantDetailScreen from './TenantDetailScreen';
import ProvisioningWizard from './ProvisioningWizard';
import AiProvidersScreen from './AiProvidersScreen';
import TenantAiPanel from './TenantAiPanel';
import AiStatementsScreen from './AiStatementsScreen';
import FeedbackInboxScreen from './FeedbackInboxScreen';
import RequirementsScreen from './RequirementsScreen';
import PlansPricingScreen from './PlansPricingScreen';
import EnquiriesInboxScreen, { type EnquirySummary } from './EnquiriesInboxScreen';
import { platformApi } from '../lib/platformApi/client';

type View =
  | { name: 'list' }
  | { name: 'detail'; tenantId: number }
  | { name: 'provision'; from?: EnquirySummary }
  | { name: 'ai-providers' }
  | { name: 'ai-plan'; tenantId: number; tenantSlug: string; tenantName: string }
  | { name: 'ai-statements' }
  | { name: 'feedback' }
  | { name: 'requirements' }
  | { name: 'plans-pricing' }
  | { name: 'enquiries' };

/** A web-address suggestion from a hospital's name: "Asha Clinic, Erode" → "asha-clinic-erode". The operator can change it. */
function suggestSlug(name: string) {
  const s = name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/^[^a-z]+/, '').slice(0, 40).replace(/-+$/, '');
  return s.length >= 3 ? s : '';
}

function ConsoleShell() {
  const { operator, checking } = usePlatformAuth();
  const [view, setView] = useState<View>({ name: 'list' });

  if (checking) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', fontSize: 13 }}>Loading…</div>;
  }
  if (!operator) return <LoginScreen />;

  if (view.name === 'detail') {
    return (
      <TenantDetailScreen
        tenantId={view.tenantId}
        onBack={() => setView({ name: 'list' })}
        onOpenAiPlan={(slug, name) => setView({ name: 'ai-plan', tenantId: view.tenantId, tenantSlug: slug, tenantName: name })}
      />
    );
  }
  if (view.name === 'provision') {
    const from = view.from;
    const back = () => setView(from ? { name: 'enquiries' } : { name: 'list' });
    return (
      <ProvisioningWizard
        onBack={back}
        onDone={back}
        initial={from ? { name: from.organisation || '', slug: suggestSlug(from.organisation || '') } : undefined}
        backLabel={from ? '← Back to enquiries' : undefined}
        context={from ? `From website enquiry ${from.ref}. When it finishes, the enquiry is marked Won and linked.` : undefined}
        onProvisioned={from ? (t) => platformApi.post(`/enquiries/${from.id}/link-tenant`, { tenant_id: t.id }).then(() => undefined) : undefined}
      />
    );
  }
  if (view.name === 'enquiries') {
    return <EnquiriesInboxScreen onBack={() => setView({ name: 'list' })} onConvert={(e) => setView({ name: 'provision', from: e })} />;
  }
  if (view.name === 'plans-pricing') {
    return <PlansPricingScreen onBack={() => setView({ name: 'list' })} />;
  }
  if (view.name === 'ai-providers') {
    return <AiProvidersScreen onBack={() => setView({ name: 'list' })} />;
  }
  if (view.name === 'ai-plan') {
    return (
      <TenantAiPanel
        tenantId={view.tenantId}
        tenantSlug={view.tenantSlug}
        tenantName={view.tenantName}
        onBack={() => setView({ name: 'detail', tenantId: view.tenantId })}
      />
    );
  }
  if (view.name === 'ai-statements') {
    return <AiStatementsScreen onBack={() => setView({ name: 'list' })} />;
  }
  if (view.name === 'feedback') {
    return <FeedbackInboxScreen onBack={() => setView({ name: 'list' })} onOpenRequirements={() => setView({ name: 'requirements' })} />;
  }
  if (view.name === 'requirements') {
    return <RequirementsScreen onBack={() => setView({ name: 'list' })} onOpenFeedback={() => setView({ name: 'feedback' })} />;
  }
  return (
    <TenantListScreen
      onOpenTenant={(id) => setView({ name: 'detail', tenantId: id })}
      onProvisionNew={() => setView({ name: 'provision' })}
      onOpenAiProviders={() => setView({ name: 'ai-providers' })}
      onOpenAiStatements={() => setView({ name: 'ai-statements' })}
      onOpenFeedback={() => setView({ name: 'feedback' })}
      onOpenRequirements={() => setView({ name: 'requirements' })}
      onOpenPlansPricing={() => setView({ name: 'plans-pricing' })}
      onOpenEnquiries={() => setView({ name: 'enquiries' })}
    />
  );
}

export default function PlatformConsole() {
  return (
    <PlatformAuthProvider>
      <ConsoleShell />
    </PlatformAuthProvider>
  );
}
